/**
 * UI-8d Task 5 — PlatformAuditLogPage.
 *
 * Read-only viewer for the platform-wide audit log
 * (`/platform/audit-log`). Composition mirrors the UI-7a tenant
 * `AuditLogPage`, with two material differences:
 *
 *   1. Permission is `platform:audit_view` (NOT tenant `audit:view`) —
 *      gated client-side via <RequirePermission>. The backend enforces
 *      the same grant server-side.
 *   2. The shared UI-7a components are passed `source="platform"`:
 *      `AuditLogFilterSidebar` adjusts the Actor placeholder to hint at
 *      `system:*` actors, `VerifyChainBadge` POSTs to the platform
 *      verify-chain endpoint, and `AuditLogDetailDrawer` accepts the
 *      prop for symmetry (same JSON shape applies today).
 *
 * Pagination uses the same lazy/virtual pattern as the tenant page:
 *   - PrimeReact DataTable with virtualScrollerOptions
 *     ({ itemSize: 44, lazy: true, onLazyLoad → loadNextPage }).
 *   - useAuditLog → usePlatformAuditLog (same shape, different endpoint).
 *
 * Hash column truncates to first 16 hex + U+2026 ellipsis; the full
 * hash + payload live in the detail drawer.
 */
import { useState, type JSX } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { ClipboardList } from 'lucide-react'

import RequirePermission from '../../components/auth/RequirePermission.js'
import { AuditLogFilterSidebar } from '../../components/admin/compliance/AuditLogFilterSidebar.js'
import { AuditLogDetailDrawer } from '../../components/admin/compliance/AuditLogDetailDrawer.js'
import { VerifyChainBadge } from '../../components/admin/compliance/VerifyChainBadge.js'
import {
  usePlatformAuditLog,
  type AuditEntry,
  type AuditFilters,
} from '../../hooks/usePlatformAuditLog.js'
import { formatDateTime } from '../../utils/formatDate.js'

export default function PlatformAuditLogPage(): JSX.Element {
  return (
    <RequirePermission permission="platform:audit_view">
      <PlatformAuditLogPageInner />
    </RequirePermission>
  )
}

function PlatformAuditLogPageInner(): JSX.Element {
  const [filters, setFilters] = useState<AuditFilters>({})
  const { rows, loading, error, loadNextPage } = usePlatformAuditLog(filters)
  const [selected, setSelected] = useState<AuditEntry | null>(null)

  return (
    <div className="flex h-full">
      <AuditLogFilterSidebar
        value={filters}
        onChange={setFilters}
        source="platform"
      />

      <div className="flex-1 px-6 py-6 pb-10 overflow-y-auto space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-sm bg-[#3454cf]/5 flex items-center justify-center shrink-0">
              <ClipboardList className="h-5 w-5 text-[#3454cf]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Platform Audit Log</h1>
              <p className="text-sm text-gray-400 mt-0.5">Append-only audit trail. Hash-chained per row.</p>
            </div>
          </div>
          <VerifyChainBadge source="platform" />
        </div>

        {error !== null && (
          <div role="alert" className="rounded-xl bg-red-50 border border-red-100 px-5 py-3 text-sm text-red-600 font-medium">
            {error}
          </div>
        )}

        <DataTable
          value={rows}
          dataKey="id"
          scrollable
          scrollHeight="flex"
          loading={loading}
          stripedRows
          className="shadow-[0_2px_12px_rgba(0,0,0,0.04)] rounded-sm border-gray-100"
          virtualScrollerOptions={{
            itemSize: 44,
            lazy: true,
            onLazyLoad: () => { void loadNextPage() },
          }}
          onRowClick={(e) => setSelected(e.data as AuditEntry)}
          emptyMessage="No audit entries."
        >
          <Column
            field="createdAt"
            header="Time"
            body={(r: AuditEntry) => <span className="text-xs text-gray-500 whitespace-nowrap">{formatDateTime(r.createdAt)}</span>}
          />
          <Column
            field="actorId"
            header="Actor"
            body={(r: AuditEntry) => (
              <div>
                <span className="text-sm font-medium text-gray-800">{r.actorId ?? '—'}</span>
              </div>
            )}
          />
          <Column
            field="action"
            header="Action"
            body={(r: AuditEntry) => <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-[#3454cf]/5 text-[#3454cf]">{r.action}</span>}
          />
          <Column
            header="Resource"
            body={(r: AuditEntry) => (
              <span className="text-xs text-gray-500">{r.resourceType ? `${r.resourceType}/${r.resourceId ?? '—'}` : '—'}</span>
            )}
          />
          <Column
            field="hash"
            header="Hash"
            body={(r: AuditEntry) => <span className="text-xs font-mono text-gray-400">{r.hash.slice(0, 16)}…</span>}
          />
        </DataTable>

        <AuditLogDetailDrawer
          entry={selected}
          onClose={() => setSelected(null)}
          source="platform"
        />
      </div>
    </div>
  )
}
